/**
 * A card's scope (server/scope.js) on screen. Warnings that change how the
 * numbers read are always visible under the title: a filter you set that did
 * not reach this card, or a window that starts before a source does. The
 * Scope button opens the rest: dates, filters, population, sources and
 * refresh times, coverage.
 */

import { useState } from "react";
import { FILTER_LABELS, MEASURE_HINTS, MEASURE_LABELS, MEASURE_TONES, filterLabels, sourceLine, testUsersLine, windowLines } from "../lib/scope.js";
import { Button, StatusPill } from "./primitives.jsx";

/** The card's measurement state beside its title. "Measured" (the default) shows no badge. */
export function MeasureBadge({ widget }) {
  const m = widget.measure;
  if (!m || m === "measured") return null;
  return (
    <span className="ml-1.5 align-middle" title={widget.measureNote ?? MEASURE_HINTS[m]}>
      <StatusPill tone={MEASURE_TONES[m]}>{MEASURE_LABELS[m]}</StatusPill>
    </span>
  );
}

const warn = { color: "var(--status-warning)", background: "var(--status-warning-bg)" };

/** Always-visible chips. Renders nothing when the card is fully in scope. */
export function ScopeWarnings({ scope }) {
  if (!scope) return null;
  const missing = scope.filters?.notApplied ?? [];
  const history = (scope.notes ?? []).filter((n) => n.kind === "history");
  if (!missing.length && !history.length) return null;
  return (
    <div className="-mt-1.5 mb-2.5 flex flex-wrap gap-1">
      {missing.length > 0 && (
        <span className="rounded-full px-2 py-0.5 text-[11px] font-medium" style={warn} title="These filters are set on the page but this card's source cannot apply them, so its numbers include everyone.">
          Not filtered by {missing.map((k) => FILTER_LABELS[k] ?? k).join(", ")}
        </span>
      )}
      {history.map((n) => (
        <span key={n.text} className="rounded-full px-2 py-0.5 text-[11px]" style={warn}>
          {n.text}
        </span>
      ))}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-2 py-1">
      <dt className="text-ink-3">{label}</dt>
      <dd className="text-ink-2">{children}</dd>
    </div>
  );
}

/** The full scope, shown when the card's Scope button is on. `page` = the report response. */
export function ScopePanel({ widget, page }) {
  const scope = widget.scope;
  if (!scope) return null;
  const set = page?.filters ?? {};
  const dates = windowLines(scope, page);
  const pop = testUsersLine(scope);
  return (
    <dl className="mb-3 animate-fade-in rounded-control border border-line bg-surface-2 px-3.5 py-2.5 text-[11.5px]" aria-label="Scope of this card">
      {widget.info && <Row label="Counts">{widget.info}</Row>}
      {widget.measure && (
        <Row label="Measurement">
          {MEASURE_LABELS[widget.measure] ?? widget.measure} · {widget.measureNote ?? MEASURE_HINTS[widget.measure]}
        </Row>
      )}
      {dates.length > 0 && (
        <Row label="Dates">
          {dates.map((d) => (
            <div key={d}>{d}</div>
          ))}
        </Row>
      )}
      <Row label="Filters applied">{scope.filters.applied.length ? filterLabels(scope.filters.applied, set).join(" · ") : Object.keys(set).length ? "None of the filters set" : "No filters set"}</Row>
      {scope.filters.notApplied.length > 0 && (
        <Row label="Not applied">
          <span style={{ color: "var(--status-warning)" }}>{filterLabels(scope.filters.notApplied, set).join(" · ")}</span> — this card counts everyone for these.
        </Row>
      )}
      {(page?.tenant || pop) && <Row label="Population">{[page?.tenant?.name, pop].filter(Boolean).join(" · ")}</Row>}
      {scope.sources.length > 0 && (
        <Row label="Sources">
          {scope.sources.map((s) => (
            <div key={s.key}>{sourceLine(s)}</div>
          ))}
        </Row>
      )}
      {scope.notes.length > 0 && (
        <Row label="Coverage">
          {scope.notes.map((n) => (
            <div key={n.text}>{n.text}</div>
          ))}
        </Row>
      )}
    </dl>
  );
}

/**
 * The comparison-scope prompt (mockup screen 14): when some cards on the page
 * cannot apply a filter that is set, say so once at the top and let the reader
 * choose: remove those filters (every card then counts the same population) or
 * keep separate scopes (each affected card keeps its "Not filtered by" chip).
 * The choice to keep is remembered for this page and filter set, per tab session.
 */
export function ScopeBanner({ page, onRemove }) {
  const missing = new Map();
  for (const w of page?.widgets ?? []) for (const k of w.scope?.filters?.notApplied ?? []) missing.set(k, (missing.get(k) ?? 0) + 1);
  const keys = [...missing.keys()].sort();
  const id = `content-analytics:scope-kept:${page?.report}:${keys.map((k) => `${k}=${page?.filters?.[k] ?? ""}`).join("&")}`;
  const [kept, setKept] = useState(() => {
    try {
      return sessionStorage.getItem(id) === "1";
    } catch {
      return false;
    }
  });
  if (!keys.length || kept) return null;
  const cards = new Set((page?.widgets ?? []).filter((w) => w.scope?.filters?.notApplied?.length).map((w) => w.id)).size;
  const keep = () => {
    setKept(true);
    try {
      sessionStorage.setItem(id, "1");
    } catch {
      // storage blocked: the banner hides for this view only
    }
  };
  return (
    <div className="flex animate-fade-in flex-wrap items-center gap-x-4 gap-y-2 rounded-card border px-4 py-3 text-[12.5px]" style={{ borderColor: "rgb(154 97 0 / 0.25)", background: "var(--status-warning-bg)" }} role="status">
      <p className="min-w-0 flex-1 text-ink">
        <span className="font-semibold">Mixed scopes.</span> {cards} {cards === 1 ? "card" : "cards"} on this page cannot apply{" "}
        {keys.map((k) => `${FILTER_LABELS[k] ?? k}${page?.filters?.[k] ? ` (${page.filters[k]})` : ""}`).join(", ")}: they count everyone for {keys.length === 1 ? "it" : "them"}. Compare them with the other cards only after choosing a scope.
      </p>
      <div className="flex shrink-0 gap-2">
        <Button size="sm" onClick={() => onRemove(keys)}>
          Remove {keys.length === 1 ? "this filter" : "these filters"}
        </Button>
        <Button size="sm" variant="ghost" onClick={keep}>
          Keep separate scopes
        </Button>
      </div>
    </div>
  );
}
